import { Injectable, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Action,
  createPropertySelectors,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { finalize, tap } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import {
  ClearState,
  GetUserInfo,
  Login,
  LoginAdfs,
  Logout,
  S11Exchange,
  SaveToken,
  SaveUserDetail,
} from './auth.actions';
import {
  AuthResponse,
  AuthStateModel,
  S11ExchangeResponse,
} from './auth.model';
import { Observable } from 'rxjs';
import { NavbarService } from '@core/components/navbar/navbar.service';
import { Nullable } from '@shared/models/common.model';
import { Person } from '@shared/models/user.model';
import * as Sentry from '@sentry/angular';

const defaultData: Nullable<AuthStateModel> = {
  refresh: null,
  access: null,
  is_register: null,
  is_password_valid: null,
  role: null,
  session_name: null,
  user_info: null,
};

@State<Nullable<AuthStateModel>>({
  name: 'auth',
  defaults: defaultData,
})
@Injectable({
  providedIn: 'root',
})
export class AuthState {
  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private spinner: SpinnerService,
    private store: Store,
    private zone: NgZone,
    private navbar: NavbarService,
    private activated: ActivatedRoute,
  ) {}

  @Selector()
  static token(state: AuthStateModel): AuthStateModel {
    return state;
  }

  setUserDataToStorage(result: Person): void {
    localStorage.setItem('username', result.user.username);
    localStorage.setItem('photoUrl', result.photo || '');
    localStorage.setItem('pid', result.id + '');
    // setup sentry context after user logs in
    Sentry.setUser({
      username: result.user.username,
    });
  }

  updateLocalStorage(result: AuthResponse): void {
    sessionStorage.setItem('session_name', result.session_name);
    localStorage.setItem('session_id_ws', result.session_name);
    localStorage.setItem('currentUser', result.access);
    localStorage.setItem('refresh', result.refresh.replace(`"`, ''));
    localStorage.setItem(
      'is_register',
      result.is_register.toString(),
    );
    localStorage.setItem(
      'is_password_valid',
      result.is_password_valid.toString(),
    );
  }

  @Action(Login)
  login(
    { patchState }: StateContext<AuthStateModel>,
    { payload }: any,
  ): Observable<AuthResponse> {
    this.spinner.show();
    return this.authService
      .login(payload.username, payload.password)
      .pipe(
        tap((result: AuthResponse) => {
          result.role = JSON.parse(result.role)[0];
          patchState({
            refresh: result.refresh,
            access: result.access,
            is_register: result.is_register,
            is_password_valid: result.is_password_valid,
            session_name: result.session_name,
            session_id_ws: result.session_name,
            role: result.role,
          });
          this.updateLocalStorage(result);
          this.store.dispatch(GetUserInfo);
          if (!result.is_register) {
            this.zone.run(() => {
              this.router.navigate(['/register']);
            });
            return;
          }
          if (!result.is_password_valid) {
            this.zone.run(() => {
              this.router.navigate(['/reset-password']);
            });
            return;
          }
          if (
            ![
              'General',
              'Admin',
              'Contract Management',
              'Contract Management',
              'Dealer',
              'Checker',
              'S11 Controller',
              'S11 Commander',
            ].includes(result.role)
          ) {
            this.zone.runOutsideAngular(() => {
              this.zone.run(() => {
                this.router.navigate(['/users']);
              });
            });
          } else {
            this.zone.runOutsideAngular(() => {
              this.activated.queryParams.subscribe(
                (queryParams: any) => {
                  if (queryParams.returnUrlEvidence) {
                    const decodedQueryString = atob(
                      queryParams.returnUrlEvidence,
                    );
                    const queryString = JSON.parse(
                      decodedQueryString,
                    );
                    this.zone.run(() => {
                      this.router.navigate(
                        ['/contract/preview/' + queryString.id],
                        {
                          queryParams: { tab: queryString.tab },
                        },
                      );
                    });
                  } else if (
                    this.router.url.includes('/login') ||
                    this.router.url.includes('/reset-password') ||
                    this.router.url.includes('/register')
                  ) {
                    const returnUrl = queryParams.returnUrl;

                    this.zone.run(() => {
                      if (returnUrl?.includes('s11-documents')) {
                        this.router.navigateByUrl(returnUrl);
                      } else {
                        this.router.navigate(['/']);
                      }
                    });
                  }
                },
              );
            });
          }
        }),
        finalize(() => this.spinner.hide()),
      );
  }

  @Action(LoginAdfs)
  loginAdfs(
    { patchState }: StateContext<AuthStateModel>,
    { payload }: any,
  ) {
    payload.role = JSON.parse(payload.role)[0];
    patchState({
      refresh: payload.refresh,
      access: payload.access,
      is_register: payload.is_register,
      is_password_valid: payload.is_password_valid,
      session_name: payload.session_name,
      role: payload.role,
    });
    this.updateLocalStorage(payload);
    this.store.dispatch(GetUserInfo);
    if (!payload.is_register) {
      this.zone.run(() => {
        this.router.navigate(['/register']);
      });
      return;
    }
    if (!payload.is_password_valid) {
      this.zone.run(() => {
        this.router.navigate(['/reset-password']);
      });
      return;
    }
    if (
      payload.role !== 'General' &&
      payload.role !== 'Dealer' &&
      payload.role !== 'Checker' &&
      payload.role !== 'S11 Controller' &&
      payload.role !== 'S11 Commander'
    ) {
      this.zone.runOutsideAngular(() => {
        this.zone.run(() => {
          this.router.navigate(['/users']);
        });
      });
    } else {
      this.zone.runOutsideAngular(() => {
        this.zone.run(() => {
          this.router.navigate(['/memos']);
        });
      });
    }
  }

  @Action(S11Exchange)
  s11Exchange(
    { patchState }: StateContext<AuthStateModel>,
    { code }: { code: string },
  ): Observable<S11ExchangeResponse> {
    this.spinner.show();
    return this.authService.s11Exchange(code).pipe(
      tap((result: S11ExchangeResponse) => {
        result.role = JSON.parse(result.role)[0];
        patchState({
          refresh: result.refresh,
          access: result.access,
          is_register: result.is_register,
          is_password_valid: result.is_password_valid,
          role: result.role,
        });
        localStorage.setItem('currentUser', result.access);
        localStorage.setItem('refresh', result.refresh);
        localStorage.setItem(
          'is_register',
          result.is_register.toString(),
        );
        localStorage.setItem(
          'is_password_valid',
          result.is_password_valid.toString(),
        );
        if (result.username) {
          localStorage.setItem('username', result.username);
        }
        if (result.s11_login_url) {
          localStorage.setItem('s11_login_url', result.s11_login_url);
        }
        this.store.dispatch(GetUserInfo);
      }),
      finalize(() => this.spinner.hide()),
    );
  }

  @Action(Logout)
  logout(
    { setState }: StateContext<Nullable<AuthStateModel>>,
    {
      isCloseTab = false,
      skipApiCall = false,
    }: { isCloseTab?: boolean; skipApiCall?: boolean },
  ) {
    const hasCurrentUser = !!localStorage.getItem('currentUser');

    const cleanup = () => {
      localStorage.clear();
      this.navbar.setActiveSidebar(false);
      setState(defaultData);
    };

    // If skipApiCall is true (e.g., when called from error handler with expired token),
    // just do cleanup without calling the API
    if (skipApiCall) {
      cleanup();
      return;
    }

    if (hasCurrentUser) {
      const logout$ = this.authService.logout();

      if (isCloseTab) {
        logout$.pipe(finalize(cleanup)).subscribe();
      } else {
        logout$.subscribe();
        cleanup();
      }
    }
  }

  @Action(ClearState)
  clearState({ setState }: StateContext<Nullable<AuthStateModel>>) {
    if (localStorage.getItem('currentUser')) {
      this.authService.logout().subscribe();
    }
    localStorage.clear();
    setState(defaultData);
  }

  @Action(GetUserInfo)
  getUser({
    setState,
  }: StateContext<AuthStateModel>): Observable<Person> {
    return this.authService.getUserInfo().pipe(
      tap((user) => {
        const newState: AuthStateModel = {
          refresh: localStorage.getItem('refresh') || '',
          access: localStorage.getItem('currentUser') || '',
          is_register: user.is_register,
          is_password_valid: user.is_password_valid,
          is_reset_password: user.is_reset_password,
          session_name: sessionStorage.getItem('session_name') || '',
          session_id_ws: localStorage.getItem('session_id_ws') || '',
          role: user.role?.name || '',
          user_info: user,
          can_sign_contract: user.can_sign_contract || false,
        };

        setState(newState);

        sessionStorage.setItem('id', user.id + '');
        sessionStorage.setItem('access', newState.access);
        this.setUserDataToStorage(user);
      }),
    );
  }

  @Action(SaveUserDetail)
  saveItem(
    { patchState }: StateContext<AuthStateModel>,
    { value, name }: { value: any; name: string },
  ): void {
    patchState({
      [name]: value,
    });
  }

  @Action(SaveToken)
  saveToken(
    { patchState }: StateContext<AuthStateModel>,
    { token }: { token: string },
  ): void {
    patchState({
      access: token,
    });
    localStorage.setItem('currentUser', token);
  }
}

export class AuthSelectors {
  static getSlices =
    createPropertySelectors<AuthStateModel>(AuthState);
}
